import type { Topology } from 'topojson-specification';
import { BaseCountry, DataMode, IndicatorPoint } from '../types/census';
import { HFModel } from '../types/content';
import { countrySnapshot, SNAPSHOT_AS_OF, SNAPSHOT_YEAR, SnapshotCountry } from '../data/countrySnapshot';
import { hfSnapshot, HF_SNAPSHOT_AS_OF } from '../data/hfSnapshot';
import { todayIso } from './format';

const WB = 'https://api.worldbank.org/v2';
const REST = 'https://restcountries.com/v3.1/all?fields=name,cca2,cca3,ccn3,region,population,area,flags,capital,independent';
const HF = 'https://huggingface.co/api/models?sort=downloads&direction=-1&limit=50';
const ATLAS = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

export const INDICATORS = {
  internet: 'IT.NET.USER.ZS',
  mobile: 'IT.CEL.SETS.P2',
  working: 'SP.POP.1564.TO.ZS',
  population: 'SP.POP.TOTL',
  male: 'SP.POP.TOTL.MA.IN',
  female: 'SP.POP.TOTL.FE.IN',
} as const;

export interface IndicatorResult {
  mode: DataMode;
  asOf: string;
  values: Record<string, IndicatorPoint>;
}

export interface SeriesResult {
  mode: DataMode;
  asOf: string;
  points: IndicatorPoint[];
}

export interface CountriesResult {
  mode: DataMode;
  asOf: string;
  rows: BaseCountry[];
}

export interface HFResult {
  mode: DataMode;
  asOf: string;
  models: HFModel[];
}

interface WBRow {
  countryiso3code: string;
  date: string;
  value: number | null;
}

interface RestCountry {
  name: { common: string };
  cca2: string;
  cca3: string;
  ccn3?: string;
  region: string;
  population: number;
  area?: number;
  flags?: { svg?: string; png?: string };
  capital?: string[];
  independent?: boolean;
}

const cache = new Map<string, Promise<unknown>>();

function memo<T>(key: string, fn: () => Promise<T>): Promise<T> {
  if (!cache.has(key)) cache.set(key, fn());
  return cache.get(key) as Promise<T>;
}

async function fetchJson<T>(url: string, ms = 15000): Promise<T> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(url, { signal: ctrl.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as T;
  } finally {
    clearTimeout(timer);
  }
}

const snapshotField: Record<string, keyof SnapshotCountry> = {
  [INDICATORS.internet]: 'internet',
  [INDICATORS.mobile]: 'mobile',
  [INDICATORS.working]: 'working',
  [INDICATORS.population]: 'population',
};

function snapshotIndicator(indicator: string): IndicatorResult {
  const field = snapshotField[indicator];
  const values: Record<string, IndicatorPoint> = {};
  if (field) {
    countrySnapshot.forEach((c) => {
      values[c.cca3] = { value: Number(c[field]), year: SNAPSHOT_YEAR };
    });
  }
  return { mode: 'snapshot', asOf: SNAPSHOT_AS_OF, values };
}

function parseWB(data: unknown): { asOf: string; rows: WBRow[] } {
  if (!Array.isArray(data) || !Array.isArray(data[1])) throw new Error('Unexpected World Bank response');
  const meta = data[0] as { lastupdated?: string };
  return { asOf: meta.lastupdated ?? todayIso(), rows: data[1] as WBRow[] };
}

export function getIndicatorAll(indicator: string): Promise<IndicatorResult> {
  return memo(`wb-all-${indicator}`, async () => {
    try {
      const raw = await fetchJson<unknown>(`${WB}/country/all/indicator/${indicator}?format=json&date=2020:2025&per_page=20000`);
      const { asOf, rows } = parseWB(raw);
      const values: Record<string, IndicatorPoint> = {};
      rows.forEach((r) => {
        if (r.value == null || !r.countryiso3code) return;
        const year = Number(r.date);
        const cur = values[r.countryiso3code];
        if (!cur || year > cur.year) values[r.countryiso3code] = { value: r.value, year };
      });
      if (Object.keys(values).length === 0) throw new Error('Empty');
      return { mode: 'live' as const, asOf, values };
    } catch {
      return snapshotIndicator(indicator);
    }
  });
}

export function getCountrySeries(cca3: string, indicator: string): Promise<SeriesResult> {
  return memo(`wb-${cca3}-${indicator}`, async () => {
    try {
      const raw = await fetchJson<unknown>(`${WB}/country/${cca3}/indicator/${indicator}?format=json&date=2015:2025&per_page=100`);
      const { asOf, rows } = parseWB(raw);
      const points = rows
        .filter((r) => r.value != null)
        .map((r) => ({ value: r.value as number, year: Number(r.date) }))
        .sort((a, b) => a.year - b.year)
        .slice(-5);
      if (points.length === 0) throw new Error('Empty');
      return { mode: 'live' as const, asOf, points };
    } catch {
      const snap = snapshotIndicator(indicator).values[cca3];
      return { mode: 'snapshot' as const, asOf: SNAPSHOT_AS_OF, points: snap ? [snap] : [] };
    }
  });
}

export function getCountries(): Promise<CountriesResult> {
  return memo('rest-countries', async () => {
    try {
      const raw = await fetchJson<RestCountry[]>(REST);
      if (!Array.isArray(raw) || raw.length === 0) throw new Error('Empty');
      const rows: BaseCountry[] = raw
        .filter((c) => c.cca3 && c.population > 0)
        .map((c) => ({
          cca3: c.cca3,
          cca2: c.cca2,
          ccn3: c.ccn3 ?? null,
          name: c.name.common,
          region: c.region,
          capital: c.capital?.[0] ?? '—',
          population: c.population,
          area: c.area ?? null,
          flag: c.flags?.svg ?? c.flags?.png ?? `https://flagcdn.com/${c.cca2.toLowerCase()}.svg`,
          independent: c.independent !== false,
        }));
      return { mode: 'live' as const, asOf: `fetched ${todayIso()}`, rows };
    } catch {
      const rows: BaseCountry[] = countrySnapshot.map((c) => ({
        cca3: c.cca3,
        cca2: c.cca2,
        ccn3: c.ccn3,
        name: c.name,
        region: c.region,
        capital: c.capital,
        population: c.population,
        area: null,
        flag: `https://flagcdn.com/${c.cca2.toLowerCase()}.svg`,
        independent: true,
      }));
      return { mode: 'snapshot' as const, asOf: SNAPSHOT_AS_OF, rows };
    }
  });
}

export function getHFModels(): Promise<HFResult> {
  return memo('hf-models', async () => {
    try {
      const raw = await fetchJson<HFModel[]>(HF);
      if (!Array.isArray(raw) || raw.length === 0) throw new Error('Empty');
      const models = raw.map((m) => ({ id: m.id, downloads: m.downloads ?? 0, likes: m.likes ?? 0, pipeline_tag: m.pipeline_tag }));
      return { mode: 'live' as const, asOf: `fetched ${todayIso()}`, models };
    } catch {
      return { mode: 'snapshot' as const, asOf: HF_SNAPSHOT_AS_OF, models: hfSnapshot };
    }
  });
}

export interface CountResult {
  mode: DataMode;
  asOf: string;
  count: number;
}

export function getOpenRouterCount(fallback: number): Promise<CountResult> {
  return memo('openrouter-models', async () => {
    try {
      const raw = await fetchJson<{ data?: unknown[] }>('https://openrouter.ai/api/v1/models');
      if (!raw || !Array.isArray(raw.data) || raw.data.length === 0) throw new Error('Empty');
      return { mode: 'live' as const, asOf: `fetched ${todayIso()}`, count: raw.data.length };
    } catch {
      return { mode: 'snapshot' as const, asOf: '2026-10-05 (embedded snapshot)', count: fallback };
    }
  });
}

export function getWorldTopology(): Promise<Topology> {
  return memo('world-atlas', () => fetchJson<Topology>(ATLAS, 20000));
}
